import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, Save, Plug, RotateCw, Trash2, RefreshCw, Plus, Pencil, X } from "lucide-react";

export default function AdminSuppliers() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null); // { mode:'create'|'edit', data:{} }
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState("");

  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ["suppliers"],
    queryFn: async () => (await api.get("/suppliers")).data,
  });

  const active = suppliers.find((s) => s.id === selectedId) || suppliers[0] || null;

  const { data: games = [] } = useQuery({
    queryKey: ["supplier-games", active?.id],
    queryFn: async () => active ? (await api.get(`/suppliers/${active.id}/games`)).data : [],
    enabled: !!active,
  });

  const { data: products = [] } = useQuery({
    queryKey: ["admin-products-suppliers"],
    queryFn: async () => (await api.get("/products/admin/list")).data,
  });

  const test = async (id) => { setBusy(`test-${id}`); try { const r = await api.post(`/suppliers/${id}/test`); toast.success(r.data.message || "OK"); qc.invalidateQueries({ queryKey: ["suppliers"] }); } catch (e) { toast.error(e?.response?.data?.detail || "Fail"); } finally { setBusy(""); } };
  const sync = async (id) => { setBusy(`sync-${id}`); try { const r = await api.post(`/suppliers/${id}/sync`); toast.success(`Synced ${r.data.count} games`); qc.invalidateQueries({ queryKey: ["suppliers"] }); qc.invalidateQueries({ queryKey: ["supplier-games"] }); } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); } finally { setBusy(""); } };
  const remove = async (id, label) => { if (!window.confirm(`Delete "${label}"? Product links to this account will be removed.`)) return; try { await api.delete(`/suppliers/${id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["suppliers"] }); qc.invalidateQueries({ queryKey: ["admin-products-suppliers"] }); } catch { toast.error("Failed"); } };
  const link = async (product_id, sg) => {
    try { await api.post(`/suppliers/${active.id}/link`, { product_id, supplier_game_id: sg.supplier_game_id, supplier_game_title: sg.title }); toast.success("Linked"); qc.invalidateQueries({ queryKey: ["admin-products-suppliers"] }); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const unlink = async (product_id) => {
    try { await api.delete(`/suppliers/${active.id}/link/${product_id}`); toast.success("Unlinked"); qc.invalidateQueries({ queryKey: ["admin-products-suppliers"] }); } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8" data-testid="admin-suppliers-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">Code Sources</div>
          <h1 className="font-heading text-3xl font-bold">Supplier Accounts</h1>
          <p className="text-sm text-zinc-500 mt-1">Add unlimited accounts. When a code is requested, the system tries the first account not on 3-day cooldown, then the next, automatically.</p>
        </div>
        <button onClick={() => setModal({ mode: "create", data: { name: "sentinus", label: "", email: "", password: "" } })} className="bg-brand hover:bg-brand-hover text-white font-semibold px-5 py-2.5 rounded-lg inline-flex items-center gap-2 glow-blue" data-testid="add-supplier-btn">
          <Plus className="w-4 h-4" /> Add Account
        </button>
      </div>

      {isLoading ? <div className="text-zinc-500">Loading…</div> : suppliers.length === 0 ? (
        <div className="bg-[#0c0c0c] border border-white/5 rounded-xl p-12 text-center text-zinc-500">No supplier accounts yet.</div>
      ) : (
        <div className="grid gap-3 mb-8">
          {suppliers.map((s) => (
            <div key={s.id} onClick={() => setSelectedId(s.id)}
                 className={`bg-[#0c0c0c] border rounded-xl p-4 flex items-center gap-4 cursor-pointer ${active?.id === s.id ? "border-brand" : "border-white/5 hover:border-white/20"}`}
                 data-testid={`supplier-row-${s.id}`}>
              <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ background: s.connected ? "#10b981" : "#71717a" }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-bold">{s.label || s.email_masked}</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-brand-cyan/15 text-brand-cyan border border-brand-cyan/30">{s.name}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold border ${s.connected ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"}`}>{s.status}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1 font-mono truncate">
                  {s.email_masked} · {s.games_count || 0} games
                  {s.last_sync_at && ` · synced ${new Date(s.last_sync_at).toLocaleString()}`}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => test(s.id)} disabled={!!busy} className="p-2 hover:bg-white/5 rounded text-zinc-400" title="Test Connection" data-testid={`supplier-test-${s.id}`}>{busy === `test-${s.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}</button>
                <button onClick={() => sync(s.id)} disabled={!!busy} className="p-2 hover:bg-brand/10 rounded text-brand-cyan" title="Sync Games" data-testid={`supplier-sync-${s.id}`}>{busy === `sync-${s.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</button>
                <button onClick={() => test(s.id)} className="p-2 hover:bg-white/5 rounded text-zinc-400" title="Reconnect" data-testid={`supplier-reconnect-${s.id}`}><RotateCw className="w-4 h-4" /></button>
                <button onClick={() => setModal({ mode: "edit", data: { id: s.id, name: s.name, label: s.label, email: s.email_masked, password: "" } })} className="p-2 hover:bg-white/5 rounded text-zinc-400" title="Edit" data-testid={`supplier-edit-${s.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(s.id, s.label || s.email_masked)} className="p-2 hover:bg-red-500/10 rounded text-red-400" title="Delete" data-testid={`supplier-remove-${s.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {active && (
        <div className="bg-[#0c0c0c] border border-white/5 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-heading text-lg font-bold">Link Products → Games from "{active.label || active.email_masked}"</div>
              <div className="text-xs text-zinc-500">A product can be linked to games from multiple supplier accounts — the system will iterate them.</div>
            </div>
            <div className="text-xs text-zinc-500">{games.length} games</div>
          </div>
          {games.length === 0 ? (
            <div className="text-sm text-zinc-500">No games synced for this account yet — click the sync icon above.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {products.map((p) => {
                const links = p.supplier_links || [];
                const linkForThis = links.find((l) => l.account_id === active.id);
                return (
                  <div key={p.id} className="bg-[#080808] border border-white/5 rounded p-3" data-testid={`link-row-${p.id}`}>
                    <div className="font-medium truncate">{p.title}</div>
                    <div className="text-xs text-zinc-500 mb-2">
                      {p.platform} · Linked to {links.length} account{links.length === 1 ? "" : "s"}
                    </div>
                    <div className="flex gap-2 items-center">
                      <select
                        value={linkForThis?.supplier_game_id || ""}
                        onChange={(e) => {
                          const g = games.find((x) => x.supplier_game_id === e.target.value);
                          if (g) link(p.id, g);
                        }}
                        className="flex-1 bg-[#0c0c0c] border border-white/10 rounded px-2 py-1.5 text-sm" data-testid={`link-select-${p.id}`}>
                        <option value="">— Not linked to this account —</option>
                        {games.map((g) => (
                          <option key={g.supplier_game_id} value={g.supplier_game_id}>{g.title || g.supplier_game_id}</option>
                        ))}
                      </select>
                      {linkForThis && <button onClick={() => unlink(p.id)} className="p-2 hover:bg-red-500/10 text-red-400 rounded" title="Unlink from this account" data-testid={`unlink-${p.id}`}><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {modal && <SupplierModal modal={modal} onClose={() => setModal(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ["suppliers"] }); setModal(null); }} />}
    </div>
  );
}

function SupplierModal({ modal, onClose, onSaved }) {
  const [f, setF] = useState(modal.data);
  const [busy, setBusy] = useState(false);
  const isEdit = modal.mode === "edit";
  const submit = async (e) => {
    e?.preventDefault();
    if (!isEdit && (!f.email || !f.password)) { toast.error("Email + password required"); return; }
    setBusy(true);
    try {
      if (isEdit) {
        const payload = {};
        if (f.label !== undefined) payload.label = f.label;
        if (f.password) payload.password = f.password;
        await api.put(`/suppliers/${f.id}`, payload);
      } else {
        await api.post(`/suppliers`, { name: f.name, label: f.label, email: f.email, password: f.password });
      }
      toast.success(isEdit ? "Updated" : "Added");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-[#0c0c0c] border border-white/10 rounded-xl max-w-md w-full" data-testid="supplier-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <h3 className="font-heading text-lg font-bold">{isEdit ? "Edit Account" : "Add Supplier Account"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {!isEdit && (
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 font-bold">Supplier</div>
              <select value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full bg-[#080808] border border-white/10 rounded px-3 py-2">
                <option value="sentinus">Sentinus Store</option>
              </select>
            </div>
          )}
          <Input label="Label (optional)" value={f.label} onChange={(v) => setF({ ...f, label: v })} testid="supplier-label" placeholder="e.g. Sentinus main / Sentinus backup" />
          <Input label="Email" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} disabled={isEdit} testid="supplier-email" />
          <Input label={isEdit ? "New Password (leave blank to keep)" : "Password"} type="password" value={f.password} onChange={(v) => setF({ ...f, password: v })} testid="supplier-password" />
          <p className="text-xs text-zinc-500">Credentials are stored encrypted and never surfaced to users or logs.</p>
        </div>
        <div className="px-6 py-4 border-t border-white/5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-white/5 hover:bg-white/10">Cancel</button>
          <button type="submit" disabled={busy} className="px-5 py-2 rounded-md bg-brand hover:bg-brand-hover font-semibold inline-flex items-center gap-2" data-testid="supplier-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text", placeholder = "", disabled = false }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 font-bold">{label}</div>
      <input type={type} value={value || ""} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#080808] border border-white/10 rounded px-3 py-2 focus:border-brand outline-none disabled:opacity-50" data-testid={testid} />
    </div>
  );
}
